/**
 * Resumable proxied-chunk upload client (ADR 0010 section 4). The server reports which parts
 * arrived (HEAD), so an interrupted upload resumes from there, also after a page reload: the
 * session id is remembered per file (name, size, modified time) in this browser only.
 */
import { safeStorageGet, safeStorageSet } from "../i18n";
import { ApiError, getCsrfToken } from "./api";

export type UploadTarget = {
  target_kind: "production_job" | "attachment";
  target_id: string;
  asset_type?: string;
  attachment_kind?: string;
};
export type UploadResult = {
  asset_id?: string;
  display_id?: string | null;
  deduplicated?: boolean;
  attachment_id?: string;
  asset_version_id?: string; // editing cuts: the new DRAFT version
  version?: number | null;
};
type Session = { id: string; part_size: number; expected_parts: number; status: string; result?: UploadResult };

const HASH_LIMIT = 256 * 1024 * 1024; // the browser hashes smaller files in memory to verify them

function rememberKey(f: File, t: UploadTarget): string {
  return `prisma.upload.${t.target_kind}.${t.target_id}.${f.name}.${f.size}.${f.lastModified}`;
}

async function send(method: string, url: string, body?: BodyInit, json = true): Promise<Response> {
  const headers: Record<string, string> = {};
  const token = getCsrfToken();
  if (method !== "GET" && method !== "HEAD" && token) headers["x-csrf-token"] = token;
  if (json && body !== undefined) headers["content-type"] = "application/json";
  let r: Response;
  try {
    r = await fetch(url, { method, headers, body, credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "NETWORK");
  }
  if (!r.ok) {
    const e = (await r.json().catch(() => ({}))) as { error?: { code?: string; details?: Record<string, unknown> } };
    throw new ApiError(r.status, e.error?.code ?? "unknown", e.error?.details);
  }
  return r;
}

async function sha256(f: File): Promise<string | null> {
  if (f.size > HASH_LIMIT || !crypto?.subtle) return null;
  const digest = await crypto.subtle.digest("SHA-256", await f.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function uploadFile(
  file: File,
  target: UploadTarget,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  const key = rememberKey(file, target);
  let session: Session | null = null;
  const remembered = safeStorageGet(key);
  if (remembered) {
    try {
      session = (await (await send("GET", `/app/v1/upload-sessions/${remembered}`)).json()) as Session;
      if (!["CREATED", "UPLOADING"].includes(session.status)) session = null;
    } catch {
      session = null;
    }
  }
  if (!session) {
    const body = JSON.stringify({ filename: file.name, content_type: file.type || "application/octet-stream", size: file.size, ...target });
    session = (await (await send("POST", "/app/v1/upload-sessions", body)).json()) as Session;
    safeStorageSet(key, session.id);
  }
  const head = await send("HEAD", `/app/v1/upload-sessions/${session.id}/parts`);
  const have = new Set((head.headers.get("x-received-parts") ?? "").split(",").filter(Boolean).map(Number));
  const total = Number(head.headers.get("x-expected-parts") ?? session.expected_parts);
  for (let n = 1; n <= total; n++) {
    if (signal?.aborted) throw new ApiError(0, "ABORTED");
    if (!have.has(n)) {
      const chunk = file.slice((n - 1) * session.part_size, n * session.part_size);
      await send("PUT", `/app/v1/upload-sessions/${session.id}/parts/${n}`, chunk, false);
    }
    onProgress(n / (total + 1));
  }
  const hash = await sha256(file);
  const done = (await (await send("POST", `/app/v1/upload-sessions/${session.id}/complete`, JSON.stringify({ sha256: hash }))).json()) as Session;
  safeStorageSet(key, "");
  onProgress(1);
  return done.result ?? {};
}
